/* eslint-disable camelcase */
import { materializeFile } from "common/artwork-file-types";
import { api } from "jiffy-api";
import DesignUploadCtaPlpPreviewClass from "./design-upload-cta-plp-preview-class";
import DesignUploadCtaPlpErrorClass from "./design-upload-cta-plp-error-class";
import DesignUploadCtaPlpCookieClass from "./design-upload-cta-plp-cookie-class";

const artworkStatusTexts = {
  preparing: {
    message: "Preparing file...",
    percentage: { start: 0, end: 5 }
  },
  initial: {
    message: "We are preparing your artwork for upload",
    percentage: { start: 5, end: 10 }
  },
  upload: {
    message: "Less than 3 minutes left",
    percentage: { start: 10, end: 35 }
  },
  validate: {
    message: "Less than 2 minutes left",
    percentage: { start: 35, end: 50 }
  },
  process: {
    message: "Less than 1 minute left",
    percentage: { start: 50, end: 95 }
  },
  complete: {
    message: "Few seconds left",
    percentage: { start: 100, end: 100 }
  }
};

export default class DesignFileUploadingClass {
  #progressSelector = ".js-upload-progress-container";

  #file;

  #triggerSource;

  #uploadSource;

  #dtfChannel;

  #productId = 432292; // DTF001

  constructor(props) {
    this.#file = props.file;
    this.#triggerSource = props.triggerSource;
    this.#dtfChannel = props.dtfChannel;
  }

  get #$progressBar() {
    return document.querySelector(this.#progressSelector);
  }

  get #$progressRunner() {
    return this.#$progressBar.querySelector(".js-progress-bar");
  }

  get #$fileProcess() {
    return this.#$progressBar.querySelector(".js-progress-file-process");
  }

  #showUploadProgress() {
    this.#$progressBar.classList.remove("hidden");
    this.#$fileProcess.textContent = `0% (Preparing file...)`;
  }

  #updateProgress(status, customPercentage) {
    const { message, percentage } = artworkStatusTexts[status];
    const process = customPercentage || percentage.end;
    this.#$progressRunner.style.width = `${process}%`;
    this.#$fileProcess.textContent = `${process}% (${message})`;
  }

  // eslint-disable-next-line max-lines-per-function
  async #uploadFile() {
    // Materialize the file to handle cloud storage providers (Google Drive, Dropbox) on mobile
    const { file: materializedFile, error: materializeError } = await materializeFile(this.#file, {
      timeoutMs: 120000, // 2 minute timeout for large files from cloud storage
      onProgress: progress => {
        // Show preparation progress (0-5% range)
        const prepProgress = Math.round(progress * 5);
        this.#$progressRunner.style.width = `${prepProgress}%`;
        this.#$fileProcess.textContent = `${prepProgress}% (Preparing file...)`;
      }
    });

    if (materializeError || !materializedFile) {
      new DesignUploadCtaPlpErrorClass(
        materializeError || "Failed to read file. Please try again."
      ).showError();
      this.#$progressBar.classList.add("hidden");
      return;
    }

    const formData = new FormData();
    const processTimeStamp = Date.now();
    formData.append("file", materializedFile);
    formData.append("product_id", this.#productId);
    formData.append("file_original_index", 0);
    formData.append("dtf_channel_uuid", this.#dtfChannel.uuid);
    formData.append("pre_cut", false);
    formData.append("light_weight_pipeline", true);
    formData.append("process_time_stamp", processTimeStamp);
    formData.append("is_made", true);

    if (this.#uploadSource) {
      formData.append("upload_source", this.#uploadSource);
    }

    try {
      this.#updateProgress("initial");
      await api.dtf.uploadArtwork(formData, {
        raw: true,
        headers: { "Content-Type": undefined },
        useAxiosForRequest: true,
        onUploadProgress: ({ progress, loaded, total }) => {
          // Calculate upload progress within the upload range (10-35%)
          const uploadProgress = 10 + Math.floor((progress || loaded / total || 0) * 25);
          this.#$progressRunner.style.width = `${uploadProgress}%`;
          this.#$fileProcess.textContent = `${uploadProgress}% (Less than 3 minutes left)`;
        }
      });
      this.#updateProgress("upload");
    } catch (error) {
      new DesignUploadCtaPlpErrorClass(
        error.response?.data?.error || "Upload failed. Please try again."
      ).showError();
      this.#$progressBar.classList.add("hidden");
    }
  }

  async processFile() {
    this.#dtfChannel.onError = error => {
      new DesignUploadCtaPlpErrorClass(error).showError();
      this.#$progressBar.classList.add("hidden");
    };

    this.#dtfChannel.onReceived = data => {
      const { action } = data;
      if (action === "upload_artwork") {
        this.#updateProgress("upload");
      } else if (action === "validate_artwork_progress") {
        this.#updateProgress("validate");
      } else if (action === "process_lightweight_artwork_progress") {
        this.#updateProgress("process");
      } else if (action === "process_lightweight_artwork") {
        this.#updateProgress("process");
      }
    };

    this.#dtfChannel.onCompleted = ({ url, id, original_image_url, original_mime }) => {
      this.#$progressBar.classList.add("hidden");
      new DesignUploadCtaPlpPreviewClass({ url }).show();
      DesignUploadCtaPlpCookieClass.cookie = {
        url,
        id,
        original_image_url,
        original_mime,
        updated_at: new Date().toISOString()
      };
      this.#updateProgress("complete");
    };

    this.#showUploadProgress();
    await this.#uploadFile();
  }
}
